import { Link } from "react-router-dom";
import {
  Leaf,
  Award,
  UtensilsCrossed,
  Flame,
  Heart,
  ArrowRight,
  Phone,
  Quote,
} from "lucide-react";
import { ABOUT_PAGE } from "@/constants/testIds";
import { RESTAURANT } from "@/data/menu";
import Reveal from "@/components/site/Reveal";

const KITCHEN_IMG = "/images/about-kitchen.png";
const FOUNDER_IMG = "/images/about-founder.png";
const APP_IMG = "/images/about-app.png";

const pillars = [
  {
    icon: Leaf,
    title: "100% Pure Vegetarian",
    body: "Three decades. Not a single non-vegetarian item has ever passed through our kitchen. Ever.",
  },
  {
    icon: UtensilsCrossed,
    title: "Recipes That Stayed",
    body: "The same dal, the same misal, the same tempering — handed down across the family in handwritten diaries.",
  },
  {
    icon: Flame,
    title: "Slow-cooked, Daily",
    body: "Dal that simmers from dawn. Sabji that's prepped fresh every morning. No shortcuts, no day-old stock.",
  },
  {
    icon: Heart,
    title: "A Family Kitchen",
    body: "Run by the Bishnoi family for 34 years. Many of our cooks have been with us for over two decades.",
  },
];

const milestones = [
  {
    year: "1991",
    title: "Doors open at Parihar Chowk",
    body: "Shri Bhivlal Bishnoi starts a modest Giridhar Amruttulya in Aundh — a simple space for fresh chai, warm company, and good food.",
  },
  {
    year: "1995",
    title: "Kitchen expands, seating doubles",
    body: "Word spreads across Pune. Regulars from neighbouring colonies fill the chairs daily. The kitchen is enlarged to keep up with the growing appetite.",
  },
  {
    year: "2000",
    title: "A decade of Amruttulya",
    body: "Ten years of serving Pune. The name Giridhar Amruttulya becomes synonymous with an honest, home-style vegetarian meal in the city.",
  },
  {
    year: "2001",
    title: "Giridhar Veg Restaurant is born",
    body: "The second generation joins the business with fresh energy. A full-fledged veg restaurant opens — a natural evolution from chai counter to full menu dining.",
  },
  {
    year: "2006",
    title: "Wood-fired tandoor arrives",
    body: "Rotis, naans, and stuffed parathas join the menu — baked fresh to order in a new in-house tandoor, adding warmth and theatre to every meal.",
  },
  {
    year: "2014",
    title: "Recipes passed down",
    body: "The second generation deepens its roots — learning the original recipes from the family elders, ensuring the flavours that built Giridhar remain unchanged.",
  },
  {
    year: "2023",
    title: "Three decades of pure veg",
    body: "We quietly cross 32 years — same recipes, same hands, same address. Regulars now bring in their grandchildren, completing a beautiful family circle.",
  },
  {
    year: "July 2026",
    title: "Giridhar mobile app launches",
    body: "Our own delivery and takeaway app goes live — the full menu, real-time order tracking, and faster pickups, all from your phone. The future tastes familiar.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid={ABOUT_PAGE.page}>
      {/* HERO */}
      <section
        data-testid={ABOUT_PAGE.hero}
        className="relative overflow-hidden bg-cream mt-8"
      >
        <div className="container-x pt-32 sm:pt-36 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">
              Our Story
            </p>
            <h1 className="mt-4 font-serif text-5xl sm:text-6xl text-ink leading-[1.05] text-balance">
              Thirty-four years of
              <br />
              <span className="italic">pure vegetarian.</span>
            </h1>
            <p className="mt-6 text-lg text-ink-muted max-w-xl leading-relaxed">
              Giridhar Veg Restaurant has stood at Parihar Chowk in Aundh since
              1991 — quietly, generously, and on the same plot of land. This is
              the long version of how a twelve-seater kitchen became a Pune
              institution.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
              <div>
                <p className="font-serif text-4xl text-brand">34+</p>
                <p className="uppercase tracking-[0.18em] text-[0.7rem] mt-1 text-ink-muted">
                  Years
                </p>
              </div>
              <div>
                <p className="font-serif text-4xl text-brand">40+</p>
                <p className="uppercase tracking-[0.18em] text-[0.7rem] mt-1 text-ink-muted">
                  Veg dishes
                </p>
              </div>
              <div>
                <p className="font-serif text-4xl text-brand">1</p>
                <p className="uppercase tracking-[0.18em] text-[0.7rem] mt-1 text-ink-muted">
                  Family, since day one
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-5">
            <div className="relative">
              <div
                className="absolute -inset-5 bg-brand/8 rounded-[2.5rem] rotate-2"
                aria-hidden="true"
              />
              <img
                src={KITCHEN_IMG}
                alt="Hands kneading dough in the Giridhar kitchen at dawn"
                className="relative w-full h-[440px] lg:h-[520px] object-cover rounded-[2rem] shadow-xl"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section
        data-testid={ABOUT_PAGE.story}
        className="container-x section-y"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">
              The Founding
            </p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.08] text-balance">
              It started with one stove, one cook, and a promise.
            </h2>
            <div className="hairline my-8 w-24" />
            <figure className="rounded-3xl bg-cream-deep/60 border border-borderc/60 p-7">
              <Quote className="w-7 h-7 text-brand/70" />
              <blockquote className="mt-4 font-serif text-lg sm:text-xl text-ink italic leading-relaxed">
                "Veg food cooked with the same love every single day. That was
                the only rule. I never wanted to be a chain. I wanted to be a
                kitchen that families could trust."
              </blockquote>
              <figcaption className="mt-5 text-sm">
                <span className="font-medium text-ink">Shri Bhivlal Bishnoi</span>
                <span className="text-ink-muted"> · Founder, 1991</span>
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-7 space-y-5 text-ink-muted text-base sm:text-lg leading-relaxed">
            <p>
              In 1991, Shri Bihvlal Bishnoi rolled the shutter up for the first time at Shop No. 7, Parihar Chowk Aundh. He had one intention: to build the kind of vegetarian restaurant where every person who walked in felt like they were eating a home-cooked meal.
            </p>
            <p>
              The first regulars were college students and office workers from Aundh who came in for chai and misal pav on their way to work. By 1995, the queues began stretching outside on Sunday mornings. Bihvlal-ji never raised prices to slow them down — he expanded the kitchen instead.
            </p>
            <p>
              Over the next three decades, the menu grew from a handful of items to over one hundred and twenty. The second generation of the family stepped in around 2001. The wood-fired tandoor arrived in 2006. But the dal that simmers on our stove from 5:30 in the morning has not changed its recipe. Not once.
            </p>
            <p>
              That is the entire story, really. A family, a kitchen, and a quiet refusal to take shortcuts.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section
        data-testid={ABOUT_PAGE.pillars}
        className="bg-cream-deep/60"
      >
        <div className="container-x section-y">
          <Reveal>
            <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">
              What Makes Our Food, Ours
            </p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.08] max-w-2xl text-balance">
              Four things have never changed in thirty-four years.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <article
                  data-testid={ABOUT_PAGE.pillar}
                  className="h-full bg-white rounded-3xl p-7 border border-borderc/60 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-brand/10 text-brand">
                    <p.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-ink leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base text-ink-muted leading-relaxed">
                    {p.body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section
        data-testid={ABOUT_PAGE.founder}
        className="container-x section-y grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center"
      >
        <Reveal className="lg:col-span-5">
          <div className="relative">
            <div
              className="absolute -inset-5 bg-accent2/10 rounded-[2.5rem] -rotate-2"
              aria-hidden="true"
            />
            <img
              src={FOUNDER_IMG}
              alt="Shri Zumbar Bishnoi, founder of Giridhar Veg Restaurant"
              className="relative w-full h-[520px] object-cover rounded-[2rem] shadow-xl"
              loading="lazy"
            />
          </div>
        </Reveal>

        <Reveal delay={120} className="lg:col-span-7">
          <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">
            The Founder
          </p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.08] text-balance">
            Bhivlal Bishnoi — the cook,
            <br /> the host, the gatekeeper.
          </h2>
          <p className="mt-6 text-ink-muted text-base sm:text-lg leading-relaxed">
            Bhivlal-ji did not arrive in the kitchen by a straight road. He came to Pune in 1956 and spent decades doing honest, unglamorous work — first as a bicycle mechanic, then running a grocery store for thirty-five years. He learned early that showing up with consistency is its own kind of craft.
          </p>
          <p className="mt-4 text-ink-muted text-base sm:text-lg leading-relaxed">
            In 1991, at an age when most men slow down, he opened Giridhar Amruttulya at Parihar Chowk. The same discipline that ran his shop for three decades went straight into the kitchen — no shortcuts, no compromise, no day off from quality.
          </p>
          <p className="mt-4 text-ink-muted text-base sm:text-lg leading-relaxed">
            By 2001, the second generation of the Bishnoi family stepped in, and Giridhar Veg Restaurant was born. The business grew, but the standard never moved.
          </p>

          <dl className="mt-10 grid grid-cols-2 gap-6 max-w-xl">
            <div>
              <dt className="text-xs uppercase tracking-[0.2em] text-ink-muted">
                Joined the kitchen
              </dt>
              <dd className="mt-1 font-serif text-2xl text-ink">1991</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.2em] text-ink-muted">
                Hometown
              </dt>
              <dd className="mt-1 font-serif text-2xl text-ink">Pune, via hard work</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.2em] text-ink-muted">
                Signature dish
              </dt>
              <dd className="mt-1 font-serif text-2xl text-ink">Everything on the menu</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.2em] text-ink-muted">
                Still does daily
              </dt>
              <dd className="mt-1 font-serif text-2xl text-ink">Tastes the dal</dd>
            </div>
          </dl>
        </Reveal>
      </section>

      {/* TIMELINE */}
      <section
        data-testid={ABOUT_PAGE.timeline}
        className="bg-[#0F1F11] text-cream"
      >
        <div className="container-x section-y">
          <Reveal>
            <p className="text-xs tracking-[0.24em] uppercase text-white/80 font-medium">
              Milestones
            </p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-cream leading-[1.08] max-w-2xl text-balance">
              A quiet, slow, three-decade rise.
            </h2>
          </Reveal>

          <ol className="relative mt-16 max-w-3xl mx-auto">
            <span
              aria-hidden="true"
              className="absolute left-3 sm:left-1/2 top-0 bottom-0 w-px bg-cream/15 sm:-translate-x-1/2"
            />
            {milestones.map((m, i) => {
              const right = i % 2 === 1;
              return (
                <li
                  key={m.year}
                  data-testid={ABOUT_PAGE.timelineItem}
                  className="relative pl-12 sm:pl-0 sm:grid sm:grid-cols-2 sm:gap-12 mb-12 last:mb-0"
                >
                  <span
                    aria-hidden="true"
                    className={`absolute left-3 sm:left-1/2 sm:-translate-x-1/2 mt-2 w-3 h-3 rounded-full ${
                      m.highlight ? "bg-accent2 ring-4 ring-accent2/20" : "bg-brand"
                    }`}
                  />
                  <Reveal
                    delay={i * 80}
                    className={`${
                      right ? "sm:col-start-2 sm:pl-10" : "sm:pr-10 sm:text-right"
                    }`}
                  >
                    <p
                      className={`text-xs tracking-[0.24em] uppercase ${
                        m.highlight ? "text-accent2" : "text-white/80"
                      }`}
                    >
                      {m.year}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl text-cream leading-tight">
                      {m.title}
                    </h3>
                    <p className="mt-3 text-cream/65 leading-relaxed">
                      {m.body}
                    </p>
                    {m.highlight && (
                      <p className="mt-3 inline-flex items-center gap-1.5 text-[0.72rem] uppercase tracking-[0.18em] text-accent2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent2" />
                        Now live
                      </p>
                    )}
                  </Reveal>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section data-testid={ABOUT_PAGE.cta} className="container-x pb-24 mt-24">
        <Reveal>
          <div className="rounded-[2.5rem] bg-cream-deep/60 border border-borderc/60 px-8 sm:px-14 py-14 sm:py-16 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-xl text-center lg:text-left">
              <h2 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">
                Now you know us. Come eat with us.
              </h2>
              <p className="mt-3 text-ink-muted">
                Walk in for a thali, call ahead for takeaway, or just stop by
                for chai. The first table is always reserved for the curious.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover text-cream px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
              >
                See the Menu <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={`tel:${RESTAURANT.phoneRaw}`}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 text-ink hover:bg-ink/5 px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
              >
                <Phone className="w-4 h-4" /> {RESTAURANT.phone}
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
